import { useQuery } from "@tanstack/react-query"
import { apiFetch } from "./utils"

export type MetricFormat = "currency" | "percent" | "multiple"
export type Direction = "up" | "down" | null

export interface Metric {
  value: number | null
  format: MetricFormat
  delta_pct?: number | null
  delta_bps?: number | null
  dir: Direction
  methodology?: string
}

export interface Kpis {
  total_nav: Metric
  net_irr_itd: Metric
  blended_moic: Metric
  total_pl_q: Metric
  net_cash_flow_q: Metric
}

export interface NarrativeActivityItem {
  tag: string
  text: string
}

export interface NarrativeAttributionItem {
  label: string
  text: string
}

export interface Narrative {
  generated: "templated"
  paragraphs: string[]
  activity: NarrativeActivityItem[]
  attribution: NarrativeAttributionItem[]
}

export interface ExposureSlice {
  label: string
  mv: number
  cost?: number
}

export interface SegmentExposure {
  vintage: ExposureSlice[]
  asset_class: ExposureSlice[]
  age: ExposureSlice[]
}

export interface SeriesSummary {
  id: string
  name: string
  vintage: number | null
  asset_class_label: string | null
  sector: string | null
  mv: number
  cost: number
  itd_pl: number
  qoq_pl: number
  called_q: number
  dist_q: number
  irr: number | null
  moic: number | null
  pct_of_portfolio: number | null
}

export interface CompanySummary {
  id: string
  name: string
  description: string | null
  sector: string | null
  asset_class_label: string | null
  region: string | null
  mv: number
  cost: number
  pl: number
  moic: number | null
  primary_series: string
  extra_series_count: number
  pct_of_portfolio: number | null
}

export interface PortfolioSummaryData {
  period: { year: number; quarter: number; label: string }
  available_periods: { year: number; quarter: number; label: string }[]
  status: "Complete" | "Pending"
  kpis: Kpis
  narrative: Narrative
  gics_exposure: ExposureSlice[]
  geographic_exposure: ExposureSlice[]
  segment_exposure: SegmentExposure
  series: SeriesSummary[]
  companies: CompanySummary[]
  // Present when the cache is being built asynchronously (HTTP 202)
  building?: true
}

export const fetchPortfolioSummary = (year: number, quarter: number) =>
  apiFetch<PortfolioSummaryData>(`/api/portfolio_summaries/${year}/${quarter}`)

export const usePortfolioSummary = (year: number, quarter: number) =>
  useQuery<PortfolioSummaryData, Error>({
    queryKey: ["portfolio_summary", year, quarter],
    queryFn: () => fetchPortfolioSummary(year, quarter),
    // Poll every 3 seconds while the cache is being built
    refetchInterval: (data) => (data?.building ? 3000 : false),
  })
